import React, { forwardRef, useCallback, useImperativeHandle } from "react";
import { isFunction } from "lodash";
import { ButtonWithLoader } from "../general/ButtonWithLoader/ButtonWithLoader";
import {
    AddAccountProps,
    CreateAccountHandle,
} from "../accounts/AddAccounts/lib";
import { ExtendedButtonProps } from "../general/Button/Button";
import { useInteraction } from "../../hooks/useInteraction";
import {
    usePayPalConnectionCreationMutation,
    usePayPalMigrationFromPlaidMutation,
} from "../../api/paypal.api";
import { PlaidConnection } from "../../common/types/plaidConnection";
import { usePayPalAuthorization } from "./usePayPalAuthorization";

interface Props extends AddAccountProps {
    children?:
        | React.ReactNode
        | ((props: {
              connecting: boolean;
              initializeConnection: () => void;
          }) => React.ReactNode);
    btnVariant?: ExtendedButtonProps["variant"];
    btnSize?: ExtendedButtonProps["size"];
    className?: string;
    plaidConnectionToMigrate?: PlaidConnection;
}
export const CreatePayPalConnection = forwardRef<CreateAccountHandle, Props>(
    (
        {
            children = <>Connect</>,
            onCreated,
            defaultEntity,
            btnVariant = "tertiary",
            btnSize,
            className,
            plaidConnectionToMigrate,
        },
        ref,
    ) => {
        const connectionCreationMutation =
            usePayPalConnectionCreationMutation(onCreated);
        const migrationMutation =
            usePayPalMigrationFromPlaidMutation(onCreated);
        const createConnection = useCallback(
            (authCode: string, sharedId: string) => {
                if (plaidConnectionToMigrate) {
                    migrationMutation.mutate({
                        plaidConnectionId: plaidConnectionToMigrate.id,
                        authCode,
                        sharedId,
                    });
                } else {
                    connectionCreationMutation.mutate({
                        authCode,
                        sharedId,
                        defaultEntityId: defaultEntity?.id,
                    });
                }
            },
            [
                connectionCreationMutation,
                defaultEntity,
                plaidConnectionToMigrate,
                migrationMutation,
            ],
        );

        const { initializeConnection } =
            usePayPalAuthorization(createConnection);

        useImperativeHandle(
            ref,
            () => ({
                initializeConnection,
            }),
            [initializeConnection],
        );

        const authorize = useInteraction(initializeConnection);

        if (isFunction(children)) {
            return children({
                connecting: connectionCreationMutation.isPending,
                initializeConnection,
            });
        }

        return (
            <ButtonWithLoader
                loading={
                    connectionCreationMutation.isPending ||
                    migrationMutation.isPending
                }
                variant={btnVariant}
                size={btnSize}
                className={className}
                {...authorize}
            >
                {children}
            </ButtonWithLoader>
        );
    },
);
